/**
 * dealHealth.ts — the frontend half of 2.4 (P2-12): how a deal's SERVER-
 * computed health signals render. The signals themselves come from
 * internal/modules/sales/health — the ONE definition the 4.3 digest will
 * share — and nothing here re-derives a signal from dates.
 *
 * H-2: colour is keyed on SEVERITY, never on code. A code this bundle has
 * never seen renders its label in its severity's colour exactly like a known
 * one; there is no code→colour table to fall behind, and `code` is carried
 * only as a data attribute. Only the SEVERITY vocabulary is known here
 * (danger | warning → the existing token pairs). A severity outside it is
 * still rendered — the label is the server's statement and hiding it would
 * be dishonest — but in the neutral outline capsule with NO status colour,
 * never a guessed one.
 *
 * `health` on the wire: an ARRAY on an open deal ([] = healthy), NULL on a
 * closed deal (not evaluated), ABSENT on a read path that predates 2.4 or
 * never attaches it (the account-detail deal lists). All three render
 * nothing; the distinction is carried, not drawn (F2).
 */
import { formatDay } from './dealRail'
import { danger, warning } from '../../../../theme/tokens'
import type { DealHealthSignal, DealRiskFlag, SalesRiskFlagAction } from '../../../../api/types'

export type HealthTone = { background: string; color: string }

const SEVERITY_RANK: Record<string, number> = { danger: 0, warning: 1 }

/** The token pair for a severity, or null for one this bundle does not know
 *  (rendered neutral — never a default colour). */
export function healthTone(severity: string): HealthTone | null {
  if (severity === 'danger') return { background: danger.bg, color: danger.text }
  if (severity === 'warning') return { background: warning.bg, color: warning.text }
  return null
}

/** Severity order: danger, warning, then anything unknown — STABLE, so the
 *  server's worst-first order survives within a severity and this is a no-op
 *  on a correctly ordered array. */
export function worstFirst(signals: readonly DealHealthSignal[]): DealHealthSignal[] {
  return signals
    .map((s, i) => ({ s, i }))
    .sort((a, b) => ((SEVERITY_RANK[a.s.severity] ?? 99) - (SEVERITY_RANK[b.s.severity] ?? 99)) || a.i - b.i)
    .map(x => x.s)
}

/** What there is to render: the signals of an open deal, worst first; empty
 *  for [] (healthy), null (closed) and undefined (not on this wire). */
export function visibleSignals(health: readonly DealHealthSignal[] | null | undefined): DealHealthSignal[] {
  if (!health || health.length === 0) return []
  return worstFirst(health)
}

/** Revenue Recovery R3: the rule family each board signal code belongs to
 *  — the writer's RuleOf map (internal/salesriskflags), restated so a
 *  dismissal recorded against a FAMILY reaches the signal that fires it.
 *  '' for a code the ledger does not record (nothing dismisses it). */
export const RULE_OF_CODE: Readonly<Record<string, string>> = {
  close_overdue: 'close_date',
  stage_stale: 'stage_age',
  no_activity: 'recent_activity',
  no_activity_ever: 'recent_activity',
  single_threaded: 'contact',
  no_decision_maker: 'committee',
}
export function ruleOfCode(code: string): string {
  return RULE_OF_CODE[code] ?? ''
}

/** The rule families whose open episode's LATEST act is a dismiss. An
 *  episode nobody acted on, or whose latest act is a confirm, is not here. */
export function dismissedRules(riskFlags: readonly DealRiskFlag[] | null | undefined): Set<string> {
  const out = new Set<string>()
  for (const f of riskFlags ?? []) if (f.action?.action === 'dismiss') out.add(f.rule)
  return out
}

/** A signal is dismissed when its rule family's latest act is a dismiss. */
export function isDismissed(signal: DealHealthSignal, riskFlags: readonly DealRiskFlag[] | null | undefined): boolean {
  const rule = ruleOfCode(signal.code)
  return rule !== '' && dismissedRules(riskFlags).has(rule)
}

/** What still asks for attention: the visible signals minus the dismissed
 *  ones, worst first. The signals themselves are untouched facts — a dismissal
 *  removes nothing from `health`; it changes what the board SHOWS. */
export function undismissedSignals(health: readonly DealHealthSignal[] | null | undefined, riskFlags?: readonly DealRiskFlag[] | null): DealHealthSignal[] {
  return visibleSignals(health).filter(s => !isDismissed(s, riskFlags))
}

/** True when the deal HAS signals and every one is dismissed — the board
 *  card's one muted "Dismissed" capsule (§40.13 ruling 5): the fact stays
 *  visible, with no status colour. */
export function fullyDismissed(health: readonly DealHealthSignal[] | null | undefined, riskFlags?: readonly DealRiskFlag[] | null): boolean {
  const v = visibleSignals(health)
  return v.length > 0 && undismissedSignals(health, riskFlags).length === 0
}

/** The board card's ONE badge: the worst UNDISMISSED signal and how many more
 *  it stands for; null when nothing undismissed remains (then the card shows
 *  the Dismissed capsule if fullyDismissed, else nothing). Without riskFlags
 *  (the switch off, or a wire that lacks it) nothing is dismissed. */
export function cardBadge(health: readonly DealHealthSignal[] | null | undefined, riskFlags?: readonly DealRiskFlag[] | null): { signal: DealHealthSignal; more: number } | null {
  const v = undismissedSignals(health, riskFlags)
  if (v.length === 0) return null
  return { signal: v[0], more: v.length - 1 }
}

/** The fixed dismiss reasons (§40.13 ruling 1), slug → label, in the order
 *  the dialog lists them. The server validates the slug; "other" needs a note. */
export const DISMISS_REASONS: ReadonlyArray<{ slug: string; label: string }> = [
  { slug: 'expected_pause', label: 'Customer-side pause' },
  { slug: 'tracked_elsewhere', label: 'Tracked elsewhere' },
  { slug: 'not_a_risk', label: 'Not a risk' },
  { slug: 'already_handled', label: 'Already handled' },
  { slug: 'other', label: 'Other' },
]
/** The dismiss note (§40.13 ruling 1): its hint and its bound in characters. */
export const NOTE_HINT = "Don't include personal details"
export const NOTE_MAX = 200

export function reasonLabel(slug: string | undefined): string {
  return DISMISS_REASONS.find(r => r.slug === slug)?.label ?? (slug ?? '')
}

/** "Dismissed by Pat · Customer-side pause · 19 Oct" — the act as a line a
 *  rep can read. The name is the server's resolved display name, else the
 *  short id (dealTimeline.actorName's rule: never a fabricated name). */
export function actionLine(a: SalesRiskFlagAction, who: (userId?: string, displayName?: string) => string): string {
  const verb = a.action === 'dismiss' ? 'Dismissed' : 'Confirmed'
  const parts = [`${verb} by ${who(a.actorUserId, a.actorDisplayName)}`]
  if (a.action === 'dismiss' && a.reason) parts.push(reasonLabel(a.reason))
  const day = fmtSince(`${a.actedOn}T00:00:00Z`)
  if (day) parts.push(day)
  return parts.join(' · ')
}

/** "29 Aug" — the year only when it is not the current one; '' for a stamp
 *  that does not parse (never a fabricated date). UTC, like every other date
 *  the dashboard prints from the wire. */
export function fmtSince(iso: string, now: number = Date.now()): string {
  const t = Date.parse(iso)
  if (Number.isNaN(t)) return ''
  const d = new Date(t)
  const sameYear = d.getUTCFullYear() === new Date(now).getUTCFullYear()
  // Slice 3: the deal page's one month table (dealRail.formatDay) — the
  // runtime's en-GB prints "Sept", which spec §4's "15 Dec 2026" form never has.
  const day = formatDay(iso, 'utc')
  return sameYear ? day.replace(/ \d{4}$/, '') : day
}
